import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { Helmet } from 'react-helmet-async';
import './App.css';

const ADONISIAN_SIGNS = [
  { name: "Aries", dates: "Apr 19 - May 13", symbol: "♈︎" },
  { name: "Taurus", dates: "May 14 - Jun 19", symbol: "♉︎" },
  { name: "Gemini", dates: "Jun 20 - Jul 20", symbol: "♊︎" },
  { name: "Cancer", dates: "Jul 21 - Aug 9", symbol: "♋︎" },
  { name: "Leo", dates: "Aug 10 - Sep 15", symbol: "♌︎" },
  { name: "Virgo", dates: "Sep 16 - Oct 30", symbol: "♍︎" },
  { name: "Libra", dates: "Oct 31 - Nov 22", symbol: "♎︎" },
  { name: "Scorpio", dates: "Nov 23 - Nov 29", symbol: "♏︎" },
  { name: "Ophiuchus", dates: "Nov 30 - Dec 17", symbol: "⛎︎" },
  { name: "Sagittarius", dates: "Dec 18 - Jan 18", symbol: "♐︎" },
  { name: "Capricorn", dates: "Jan 19 - Feb 15", symbol: "♑︎" },
  { name: "Aquarius", dates: "Feb 16 - Mar 11", symbol: "♒︎" },
  { name: "Pisces", dates: "Mar 12 - Apr 18", symbol: "♓︎" }
];

// --- DYNAMIC TIME ENGINE ---
// This automatically generates "June 2026", "July 2026", "August 2026", etc.
const getMonthString = (offset) => {
  const date = new Date();
  date.setMonth(date.getMonth() + offset);
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
};

export default function Horoscopes({ session }) {
  const [selectedSign, setSelectedSign] = useState(null);
  // viewingOffset: -1 is Past, 0 is Present, 1 is Future
  const [viewingOffset, setViewingOffset] = useState(0); 
  const [dbData, setDbData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Auto-calculated strings based on today's date
  const previousMonthStr = getMonthString(-1);
  const currentMonthStr = getMonthString(0); 
  const nextMonthStr = getMonthString(1);

  useEffect(() => {
    window.scrollTo(0, 0); // Forces the viewport to the very top
    fetchHoroscopes();
  }, []);

  const fetchHoroscopes = async () => {
    setLoading(true);
    // Ask Supabase for the real data (unrestricted)
    const { data, error } = await supabase
      .from('monthly_horoscopes')
      .select('*');
      
    if (error) {
      console.error("Vault connection failed:", error);
    } else if (data) {
      setDbData(data);
    }
    setLoading(false);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <div style={{ color: '#00d4ff', textAlign: 'center', padding: '50px' }}>Loading the Matrix...</div>;
  }

  // 1. THE MAIN GRID VIEW
  if (!selectedSign) {
    return (
      <div className="horoscope-container" style={{ padding: '40px 20px', maxWidth: '1000px', margin: '0 auto', textAlign: 'center' }}>
        
        {/* 👇 MAIN HOROSCOPE HUB SEO 👇 */}
        <Helmet>
          <title>Astro-Somatic™ Horoscopes | The True 13-Sign Wheel</title>
          <meta name="description" content="Read your true 13-sign astronomical forecast. Real-time transits mapping your Health, Wealth, Love, and Cerebrospinal Fluid optimization." />
        </Helmet>

        <h2 style={{ color: '#ffca28', fontSize: '2.2rem', textTransform: 'uppercase', letterSpacing: '2px' }}>
          The 13-Sign Astro-Somatic™ Forecast
        </h2>
        <p style={{ color: '#8b949e', marginBottom: '15px', fontSize: '1.1rem' }}>
          Real-time astronomical transits mapping your Health, Wealth, Love, and Cerebrospinal Fluid (CSF) optimization.
        </p>
        <h3 style={{ color: '#00d4ff', fontSize: '1rem', fontWeight: '400', marginBottom: '40px', fontStyle: 'italic' }}>
          Stop reading horoscopes built on a false matrix. Experience somatic healing for joint pain and mental friction through our true placement technology.
        </h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
          {ADONISIAN_SIGNS.map(sign => (
            <div 
              key={sign.name} 
              onClick={() => setSelectedSign(sign.name)}
              style={{ background: '#0d1117', border: '1px solid #30363d', padding: '20px', borderRadius: '8px', cursor: 'pointer', transition: 'all 0.3s ease' }}
              className="sign-card-hover"
            >
              <div style={{ fontSize: '3rem', color: '#00d4ff', marginBottom: '10px' }}>{sign.symbol}</div>
              <h3 style={{ color: '#ffffff', margin: '0 0 5px 0' }}>{sign.name}</h3>
              <span style={{ color: '#8b949e', fontSize: '0.85rem' }}>{sign.dates}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 2. FILTERING THE REAL DATA FROM SUPABASE
  const targetMonthLabel = getMonthString(viewingOffset);
  const activeData = dbData.find(item => item.sign === selectedSign && item.month === targetMonthLabel);

  return (
    <div style={{ padding: '40px 20px', maxWidth: '800px', margin: '0 auto' }}>
      
      {/* 👇 DYNAMIC INDIVIDUAL SIGN SEO 👇 */}
      <Helmet>
        <title>{selectedSign} Horoscope | Adonisian Astro-Somatic™</title>
        <meta name="description" content={`Read the true astronomical forecast and cerebrospinal fluid protocol for ${selectedSign}.`} />
      </Helmet>

      {/* Navigation Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', marginBottom: '30px', borderBottom: '1px solid #30363d', paddingBottom: '20px' }}>
        <button onClick={() => setSelectedSign(null)} style={{ alignSelf: 'flex-start', background: 'transparent', color: '#00d4ff', border: 'none', cursor: 'pointer', fontSize: '1rem' }}>
          ← Back to All Signs
        </button>
        
        {/* Dynamic 3-Month Toggle */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', justifyContent: 'center' }}>
          <button 
            onClick={() => setViewingOffset(-1)} 
            style={{ flex: 1, padding: '10px 15px', background: viewingOffset === -1 ? '#30363d' : 'transparent', color: viewingOffset === -1 ? '#ffca28' : '#8b949e', border: '1px solid #30363d', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            <strong>The Echo</strong><br/><span style={{fontSize: '0.8rem'}}>{previousMonthStr}</span>
          </button>
          <button 
            onClick={() => setViewingOffset(0)} 
            style={{ flex: 1, padding: '10px 15px', background: viewingOffset === 0 ? '#ffca28' : 'transparent', color: viewingOffset === 0 ? '#000' : '#8b949e', border: viewingOffset === 0 ? '1px solid #ffca28' : '1px solid #30363d', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            <strong>The Now</strong><br/><span style={{fontSize: '0.8rem'}}>{currentMonthStr}</span>
          </button>
          <button 
            onClick={() => setViewingOffset(1)} 
            style={{ flex: 1, padding: '10px 15px', background: viewingOffset === 1 ? '#30363d' : 'transparent', color: viewingOffset === 1 ? '#00d4ff' : '#8b949e', border: '1px solid #30363d', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s' }}
          >
            <strong>The Horizon</strong><br/><span style={{fontSize: '0.8rem'}}>{nextMonthStr}</span>
          </button>
        </div>
      </div>

      <h2 style={{ color: '#ffca28', fontSize: '2.5rem', textTransform: 'uppercase', marginBottom: '5px', textAlign: 'center' }}>
        {selectedSign}
      </h2>
      <h3 style={{ color: '#8b949e', textAlign: 'center', marginBottom: '40px' }}>{targetMonthLabel} Forecast</h3>

      {/* MISSING DATA FALLBACK */}
      {!activeData ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', background: '#05070a', border: '1px solid #30363d', borderRadius: '8px' }}>
          <div style={{ fontSize: '3rem', marginBottom: '15px' }}>⏳</div>
          <h2 style={{ color: '#ffffff', marginBottom: '10px' }}>Awaiting Telemetry</h2>
          <p style={{ color: '#8b949e' }}>The cosmic geometry for {selectedSign} in {targetMonthLabel} is currently rendering in the Matrix. The Oracle is gathering the coordinates. Check back soon.</p>
        </div>
      ) : (
        /* THE UNLOCKED CONTENT (Completely Free!) */
        <div className="horoscope-content" style={{ color: '#c9d1d9', lineHeight: '1.8', fontSize: '1.1rem' }}>
          
          <div style={{ background: '#0d1117', borderLeft: '4px solid #00d4ff', padding: '15px 20px', marginBottom: '30px' }}>
            <h4 style={{ color: '#00d4ff', margin: '0 0 10px 0', textTransform: 'uppercase' }}>Key Cosmic Dates</h4>
            <p style={{ margin: 0, fontSize: '0.95rem' }}>{activeData.transits}</p>
          </div>

  {activeData.new_moon_text && (
            <>
              <h3 style={{ color: '#ffffff', borderBottom: '1px solid #30363d', paddingBottom: '10px', marginTop: '30px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>🌑</span> The New Moon Matrix {activeData.transits?.includes("SOLAR ECLIPSE") ? <span style={{ color: '#ffca28' }}>(& Solar Eclipse)</span> : ""}
              </h3>
              <p>{activeData.new_moon_text}</p>
            </>
          )}

          {activeData.full_moon_text && (
            <>
              <h3 style={{ color: '#ffffff', borderBottom: '1px solid #30363d', paddingBottom: '10px', marginTop: '30px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '1.5rem' }}>🌕</span> The Full Moon Matrix {activeData.transits?.includes("LUNAR ECLIPSE") ? <span style={{ color: '#ffca28' }}>(& Lunar Eclipse)</span> : ""}
              </h3>
              <p>{activeData.full_moon_text}</p>
            </>
          )}
          
          <h3 style={{ color: '#ffffff', borderBottom: '1px solid #30363d', paddingBottom: '10px', marginTop: '30px' }}>Health & Vitality</h3>
          <p>{activeData.health_text}</p>

          <h3 style={{ color: '#ffffff', borderBottom: '1px solid #30363d', paddingBottom: '10px', marginTop: '30px' }}>Love & Connections</h3>
          <p>{activeData.love_text}</p>

          <h3 style={{ color: '#ffffff', borderBottom: '1px solid #30363d', paddingBottom: '10px', marginTop: '30px' }}>Wealth & Matrix Optimization</h3>
          <p>{activeData.money_text}</p>

          <h3 style={{ color: '#ffca28', borderBottom: '1px solid #ffca28', paddingBottom: '10px', marginTop: '40px' }}>Somatic Flow & CSF Protocol</h3>
          <p>{activeData.csf_text}</p>

          {activeData.video_url && (
             <div style={{ marginTop: '30px', background: '#05070a', padding: '20px', borderRadius: '8px', border: '1px solid #30363d', textAlign: 'center' }}>
               <h4 style={{ color: '#00d4ff', marginBottom: '15px' }}>▶️ {selectedSign} Somatic Protocol Flow</h4>
               <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', maxWidth: '100%' }}>
                 <iframe 
                   style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                   src={activeData.video_url} 
                   title="Adonisian Somatic Flow" 
                   frameBorder="0" 
                   allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                   allowFullScreen
                 ></iframe>
               </div>
             </div>
          )}

          <div style={{ marginTop: '50px', textAlign: 'center' }}>
             <button onClick={handlePrint} style={{ padding: '10px 20px', background: 'transparent', color: '#8b949e', border: '1px solid #8b949e', borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s' }}>
               🖨️ Print Horoscope
             </button>
          </div>
        </div>
      )}
    </div>
  );
}